// Hideo - Italian translations: core and profile (utils, data, settings, theme, app, profiles, aurora, projects).
// Keys are the English source strings used in the code (see docs/i18n.md).
import type { Dictionary } from '../types'

const it: Dictionary = {
    'Menu': 'Menu',
    // ===== app dialogs and thumbs-up (src/stores/notices.ts), images =====
    'OK': 'OK',
    'Cancel': 'Annulla',
    'Done!': 'Fatto!',
    'You can only drop an image here (JPG, PNG, WebP…).': 'Qui puoi trascinare solo un\'immagine (JPG, PNG, WebP…).',
    "I can't use this image: save it on your computer and drop the file.": 'Non riesco a usare questa immagine: salvala sul computer e trascina il file.',
    'You can drop an image file from your computer here.': 'Qui puoi trascinare un file immagine dal computer.',
    // Stored verdicts of creators and analysed videos (shown with tv() in VerdictBadge.vue)
    'Approved': 'Promosso',
    'Rejected': 'Bocciato',
    'Approved video': 'Video Promosso',
    'Rejected video': 'Video Bocciato',

    // ===== data.js: demo data, default names, statuses =====
    'iPhone 18 Pro review': 'Recensione iPhone 18 Pro',
    'Focus on camera and battery life.': 'Focus su fotocamera e autonomia.',
    'Test 2': 'Prova 2',
    'YouTube Trends report': 'Report YouTube Trends',
    'Research': 'Ricerca',
    'Verified data, updated monthly.': 'Dati verificati, aggiornati mensilmente.',
    'Opening': 'Gancio',
    'Hooks that work': 'Hook che funzionano',
    'Start with the end result, then explain how.': 'Inizia con il risultato finale, poi spiega il come.',
    'Profile': 'Profilo',
    'My profile': 'Il mio profilo',
    'profile': 'profilo',
    'Script': 'Script',
    'Video ready': 'Video Pronto',
    'Thumbnail': 'Thumbnail',
    'Idea': 'Idea',
    'Scripting': 'In Script',
    'Recording': 'In Registrazione',
    'Editing': 'In Montaggio',
    'Ready/Published': 'Pronto/Pubblicato',
    'Preparing': 'In preparazione',
    'Creating': 'In creazione',
    'Reviewing': 'In revisione',
    'Writing': 'In scrittura',
    'Ready': 'Pronto',
    'Planning': 'In scaletta',
    'Tech check': 'Prove tecniche',
    'Announcement': 'Annuncio',
    'Ready to go': 'Pronta',
    'Preparation': 'Preparazione',
    'Production': 'Produzione',
    'Finishing': 'Rifinitura',
    // Stored device (savedOn), shown with tv()
    'phone': 'telefono',
    'computer': 'computer',

    // ===== data.js: saving and loading =====
    'Changes not saved yet (Save online in the Profiles window)': 'Modifiche non ancora salvate (Salva online nella finestra Profili)',
    'Browser storage is full: the data of this profile was not saved in the browser. Save it to Google Drive or to a file.': 'Memoria del browser piena: i dati di questo profilo non sono stati salvati nel browser. Salvali su Google Drive o in un file.',
    'The folder already contains data for the profile "{name}". Which do you want to use?': 'La cartella contiene già dei dati per il profilo "{name}". Quali vuoi usare?',
    "The folder's": 'Quelli della cartella',
    'The current ones': 'Quelli di adesso',
    'Saving the file failed.': 'Salvataggio del file non riuscito.',
    'Invalid file.': 'File non valido.',
    '⚠ This profile has unsaved changes: they will be lost if you load.': '⚠ Questo profilo ha modifiche non ancora salvate: caricando andranno perse.',
    '⚠ The file is OLDER ({date}) than the last file used here ({last}).': '⚠ Il file è PIÙ VECCHIO ({date}) dell\'ultimo file usato qui ({last}).',
    "The file doesn't contain the save date (old format).": 'Il file non contiene la data di salvataggio (formato vecchio).',
    'Saved on {date} from the {device}.': 'Salvato il {date} dal {device}.',
    'Saved on {date}.': 'Salvato il {date}.',
    'Replace all the data of the profile "{name}" with the data saved on {service}?': 'Sostituire tutti i dati del profilo "{name}" con quelli salvati su {service}?',
    'Replace all the data of the profile "{name}" with the data from the file?': 'Sostituire tutti i dati del profilo "{name}" con quelli del file?',
    'Replace all the data of the profile "{name}" with these?': 'Sostituire tutti i dati del profilo "{name}" con quelli?',
    'The file also contains the photo, bio and socials of the profile: they will be applied.': 'Il file contiene anche foto, bio e social del profilo: verranno applicati.',
    'Load': 'Carica',
    'Load this data?': 'Caricare questi dati?',
    'Loading failed: is the browser storage full?': 'Caricamento non riuscito: memoria del browser piena?',

    // ===== Settings page (SettingsPage.vue, src/features/settings.ts) =====
    'Home': 'Home',
    'Plan · Board': 'PED · Board',
    'Plan · Calendar': 'PED · Calendario',
    'Plan · Timeline': 'PED · Timeline',
    'Published videos': 'Video pubblicati',
    'Tools · Hooks': 'Strumenti · Hooks',
    'Creators': 'Creators',
    'Sources': 'Sources',
    'Automatic': 'Automatico',
    'Light': 'Chiaro',
    'Dark': 'Scuro',
    'Appearance': 'Aspetto',
    'Theme': 'Tema',
    'Main colour': 'Colore principale',
    // ===== app themes (src/features/appThemes.ts, StylePicker.vue) and their sounds (SoundPanel.vue, SoundButton.vue) =====
    'One click applies the theme. It is saved on this device.': 'Un clic e il tema si applica. Resta salvato su questo dispositivo.',
    'This theme has its own colours: choose another one to use yours.': 'Questo tema ha i suoi colori: scegline un altro per usare il tuo.',
    'Used by Default and by every theme that follows your colour.': 'Lo usano Predefinito e tutti i temi che seguono il tuo colore.',
    'Follow my device': 'Segui il dispositivo',
    'Dark or light like your phone or computer: the app switches between the theme and its twin in the other tab.': "Scuro o chiaro come il telefono o il computer: l'app passa dal tema al suo gemello nell'altra scheda.",
    'Colours': 'Colori',
    'Special effects': 'Effetti speciali',
    'High contrast': 'Alto contrasto',
    'Own colours': 'Con i loro colori',
    'These keep their own colours and background.': 'Questi tengono i loro colori e il loro sfondo.',
    'Default': 'Predefinito',
    "Hideo's own look, with your colour": "L'aspetto di Hideo, con il tuo colore",
    'Anthracite': 'Antracite',
    'neutral anthracite with your colour': 'antracite neutro con il tuo colore',
    'Deep': 'Profondo',
    'very dark shades of your colour': 'sfumature molto scure del tuo colore',
    'Blurred bubbles': 'Bolle sfocate',
    'soft lights in your colour that sway, behind glass panels': 'luci morbide del tuo colore che dondolano, dietro pannelli di vetro',
    'Fluent': 'Fluent',
    'neat corners and soft shadows': 'angoli netti e ombre morbide',
    'Nostalgia Channel night': 'Canale Nostalgia notte',
    'anthracite, scan lines and bubbles, with its own music': 'antracite, righe da vecchio televisore e bolle, con la sua musica',
    'Pure black': 'Nero assoluto',
    'black page, white text, strong borders': 'pagina nera, testo bianco, bordi marcati',
    'Bright outlines': 'Bordi accesi',
    'black page, panels outlined in your colour': 'pagina nera, pannelli bordati con il tuo colore',
    'Full colour': 'Tutto colore',
    'a dark page of your colour, black panels': 'pagina scura del tuo colore, pannelli neri',
    'Aero night': 'Aero Notte',
    'smoked glass and a night aurora': 'vetro fumé e aurora notturna',
    'Four Keys night': 'Quattro Tasti notte',
    'very dark green and four coloured keys': 'verde scurissimo e i quattro tasti colorati',
    'Pearl': 'Perla',
    'neutral light grey with your colour': 'grigio chiaro neutro con il tuo colore',
    'Tinted': 'Tinta chiara',
    'pale shades of your colour': 'sfumature tenui del tuo colore',
    'Light blurred bubbles': 'Bolle sfocate chiare',
    'soft lights in your colour that sway, on a light page': 'luci morbide del tuo colore che dondolano, su una pagina chiara',
    'Fluent light': 'Fluent chiaro',
    'neat corners and soft shadows, light': 'angoli netti e ombre morbide, chiaro',
    'Nostalgia Channel': 'Canale Nostalgia',
    'white, scan lines and bubbles, with its own music': 'bianco, righe da vecchio televisore e bolle, con la sua musica',
    'Pure white': 'Bianco assoluto',
    'white page, black text, strong borders': 'pagina bianca, testo nero, bordi marcati',
    'Bright outlines light': 'Bordi accesi chiari',
    'white page, panels outlined in your colour': 'pagina bianca, pannelli bordati con il tuo colore',
    'Ink on colour': 'Inchiostro',
    'a pale page of your colour, black text and borders': 'pagina tenue del tuo colore, testo e bordi neri',
    'Frutiger Aero': 'Frutiger Aero',
    'glossy glass and a moving aurora': 'vetro lucido e aurora che si muove',
    'Four Keys': 'Quattro Tasti',
    'white, a green glow and four coloured keys': 'bianco, alone verde e i quattro tasti colorati',
    // ===== background layer (src/features/backdrop.ts, BackdropPicker.vue) =====
    'Background': 'Sfondo',
    'Behind every page, with any theme. Saved on this device.': 'Dietro a ogni pagina, con qualsiasi tema. Resta salvato su questo dispositivo.',
    'This theme has its own background: choose another one to use these.': 'Questo tema ha il suo sfondo: scegline un altro per usare questi.',
    'This theme has its own background, so the patterns are off.': 'Questo tema ha il suo sfondo, quindi le trame sono spente.',
    'just the theme': 'solo il tema',
    'Aurora': 'Aurora',
    'soft shades of your colour that drift slowly': 'sfumature morbide del tuo colore che si muovono piano',
    'Dots': 'Puntini',
    'a fine grid of dots': 'una griglia fitta di puntini',
    'Grid': 'Griglia',
    'thin squared lines': 'righe sottili a quadretti',
    'Contour lines': 'Curve di livello',
    'like a map of mountains': 'come una mappa di montagna',
    'Blueprint': 'Progetto tecnico',
    'a technical grid with targets': 'una griglia tecnica con i mirini',
    'Glass panels': 'Pannelli di vetro',
    'Frosted panels with a light rim: the background shows through the menus and windows.': 'Pannelli smerigliati con un bordo di luce: lo sfondo si intravede dietro menu e finestre.',
    'This theme decides it.': 'Lo decide questo tema.',
    'Fine grain': 'Grana fine',
    'A very light grain, like paper: the shades look smoother.': 'Una grana leggerissima, come la carta: le sfumature sembrano più morbide.',
    'Sounds': 'Suoni',
    'Sounds off': 'Spenti',
    'Sounds on': 'Accesi',
    'Small sounds when you click, open a window or finish something. Off unless you turn them on.': 'Piccoli suoni quando clicchi, apri una finestra o finisci qualcosa. Spenti finché non li accendi tu.',
    'Background music': 'Musica di sottofondo',
    'Soft music for the theme. It starts after your first click and goes quiet during the teleprompter.': 'Musica leggera del tema. Parte dopo il tuo primo clic e tace durante il teleprompter.',
    'The music comes with the themes: choose one above.': 'La musica arriva con i temi: scegline uno qui sopra.',
    'Music': 'Musica',
    'Effects': 'Effetti',
    'Sounds: click to turn them on or off': 'Suoni: clic per accenderli o spegnerli',
    'Sounds and music': 'Suoni e musica',
    'Choose the colour': 'Scegli il colore',
    'Reset': 'Ripristina',
    'Language': 'Lingua',
    'Automatic follows the language of your browser. The page reloads when you change it.': 'Automatico segue la lingua del browser. Cambiandola, la pagina si ricarica.',
    'Startup': 'Avvio',
    'Start page': 'Pagina iniziale',
    'Where you land after choosing the profile when the app opens.': 'Dove arrivi dopo aver scelto il profilo all\'apertura dell\'app.',
    'The profile choice is always shown when the app opens.': 'All\'apertura viene sempre mostrata la scelta del profilo.',
    'Delete': 'Elimina',
    'Add item': 'Aggiungi voce',
    'Drag to change the order': 'Trascina per cambiare l\'ordine',
    '· profile "{name}"': '· profilo "{name}"',
    'Projects': 'Progetti',
    'Every project (a channel, a client, a business) has its own videos, formats, hooks, creators and sources. You choose the project to see at the top, next to the profile (on the phone, by tapping the profile); to see several projects together in the Board create a plan (the "+ Plan" button at the top of the Board).': 'Ogni progetto (un canale, un cliente, un\'attività) ha i suoi video, format, hook, creator e fonti. Il progetto da vedere si sceglie in alto, accanto al profilo (sul telefono, toccando il profilo); per vedere più progetti insieme nella Board crea un PED (pulsante "+ PED" in cima alla Board).',
    'Manage projects': 'Gestisci progetti',
    'Plan': 'PED',
    'Checklist items': 'Voci della checklist',
    'They appear in the window of every video and in the progress bar of the cards.': 'Compaiono nella finestra di ogni video e nella barra di avanzamento delle card.',
    'Content types': 'Tipi di contenuto',
    'There are five and they are fixed: each has its own fields in the window and its own second tab.': 'Sono cinque e fissi: ognuno ha i suoi campi nella finestra e la sua seconda scheda.',
    '<b>Formats</b> (e.g. "Super Mario 64", "Fun facts") belong to each project: you manage them on the Formats page.': 'I <b>format</b> (es. "Super Mario 64", "Curiosità") sono di ogni progetto: li gestisci nella pagina Format.',
    'Online saving only works from the website.': 'Il salvataggio online funziona solo dal sito.',
    'Connected: {services}. Every change goes online after a couple of seconds (and at once when you close the app), on the services where you already saved the profile or loaded it from. When the app opens, the most recent version saved from the other device arrives by itself. The icon at the top right tells you whether everything is saved.': 'Collegati: {services}. Ogni modifica va online dopo un paio di secondi (e subito quando chiudi l\'app), sui servizi dove hai già salvato il profilo o da cui l\'hai caricato. All\'apertura arriva da sola la versione più recente salvata dall\'altro dispositivo. L\'icona in alto a destra ti dice se è tutto salvato.',
    "Access to Google lasts about an hour: when it is about to expire it renews at your first tap in the app (Google's small window may appear for a moment).": 'L\'accesso a Google dura circa un\'ora: quando sta per scadere si rinnova al primo tocco nell\'app (può comparire per un attimo la finestrella di Google).',
    'No online service connected: connect one from the Profiles window (Google Drive).': 'Nessun servizio online collegato: collegalo dalla finestra Profili (Google Drive).',
    'Saves': 'Salvataggi',
    'Online autosave': 'Salvataggio automatico online',
    'Right after every change': 'Subito dopo ogni modifica',
    'Off': 'Spento',
    'Images and voice notes': 'Immagini e vocali',
    "They are separate files: online (Images and Voice notes folders of your online save, Google Drive today) and copied on this device, so they don't fill the browser storage.": 'Sono file a parte: online (cartelle Immagini e Vocali del tuo salvataggio online, oggi Google Drive) e in copia su questo dispositivo, così non riempiono la memoria del browser.',
    'Calculating…': 'Calcolo…',
    'Profile data (texts, plan, scripts…) in the browser: <b class="text-gray-300">{size}</b>': 'Dati del profilo (testi, PED, script…) nel browser: <b class="text-gray-300">{size}</b>',
    'Profiles, Google Drive and save files': 'Profili, Google Drive e file di salvataggio',
    'Tags and formats': 'Tag e format',
    'Every project has its own tags. Rename, merge or delete: it only applies inside that project. If you rename a tag with the name of an existing one, the two tags are merged.': 'Ogni progetto ha i suoi tag. Rinomina, unisci o elimina: vale solo dentro quel progetto. Rinominando un tag con il nome di uno esistente, i due tag vengono uniti.',
    'Hooks tags': 'Tag di Hooks',
    'Sources tags': 'Tag di Sources',
    'Video formats': 'Format dei video',
    'App on your phone and computer': 'App sul telefono e sul computer',
    'You are using Hideo as an installed app.': 'Stai usando Hideo come app installata.',
    'The app is installed from the online website <b>{site}</b>.': 'L\'app si installa dal sito online <b>{site}</b>.',
    'Install the app': 'Installa l\'app',
    'Once installed, Hideo opens from its icon like an app, full screen and even without internet (Google Drive needs the connection).': 'Installata, Hideo si apre dalla sua icona come un\'app, a schermo intero e anche senza internet (Google Drive richiede la connessione).',
    '<b>Android</b> (Chrome): menu ⋮ › <b>Install app</b> or <b>Add to Home screen</b>': '<b>Android</b> (Chrome): menu ⋮ › <b>Installa app</b> o <b>Aggiungi a schermata Home</b>',
    '<b>iPhone</b> (Safari): Share button › <b>Add to Home Screen</b>': '<b>iPhone</b> (Safari): pulsante Condividi › <b>Aggiungi alla schermata Home</b>',
    '<b>Computer</b> (Chrome / Edge): install icon in the address bar': '<b>Computer</b> (Chrome / Edge): icona di installazione nella barra degli indirizzi',
    'Info': 'Info',
    'Hideo · version {version}': 'Hideo · versione {version}',
    "What's new and updates": 'Novità e aggiornamenti',
    'How it works': 'Come funziona',
    'Replay the guided tour': 'Rivedi il tour guidato',
    'Privacy': 'Privacy',
    '{theme} · {dot} main colour': '{theme} · {dot} colore principale',
    'automatic': 'automatica',
    'follows your device': 'segue il dispositivo',
    'Opens on {page}': 'Si apre su {page}',
    'Only on this device': 'Solo su questo dispositivo',
    'autosave on': 'automatico acceso',
    'autosave off': 'automatico spento',
    'App': 'App',
    'Installed': 'Installata',
    'Install it on your phone': 'Installala sul telefono',
    'Desktop app': 'App per computer',
    'Data in a folder': 'Dati in una cartella',
    'Data inside the app': 'Dati dentro l\'app',
    'Keep your profiles as files in a folder of your computer: you can open them, copy them and back them up as you like. Every day a copy also goes into its backup folder.': 'Tieni i tuoi profili come file in una cartella del computer: puoi aprirli, copiarli e farne copie come vuoi. Ogni giorno una copia va anche nella sua cartella backup.',
    'Your data is in:': 'I tuoi dati sono in:',
    'Stop using the folder': 'Smetti di usare la cartella',
    'Choose a folder': 'Scegli una cartella',
    'Choose the folder for your data': 'Scegli la cartella per i tuoi dati',
    'Images and voice notes stay inside the app for now.': 'Per ora immagini e vocali restano dentro l\'app.',
    'Stop using this folder? Your data goes back inside the app; the files in the folder stay where they are.': 'Smettere di usare questa cartella? I tuoi dati tornano dentro l\'app; i file nella cartella restano dove sono.',
    'Stop using it': 'Smetti di usarla',
    'Data folder': 'Cartella dei dati',
    'Version {version}': 'Versione {version}',
    'Tap a tile to open its settings.': 'Tocca un riquadro per aprire le sue impostazioni.',
    'Images': 'Immagini',
    'Voice notes': 'Vocali',
    'On this device': 'Su questo dispositivo',
    'Delete the item "{name}" from the checklist?': 'Eliminare la voce "{name}" dalla checklist?',
    'New item': 'Nuova voce',
    '{n} project': '{n} progetto',
    '{n} projects': '{n} progetti',
    '{n} checklist item': '{n} voce di checklist',
    '{n} checklist items': '{n} voci di checklist',
    '{n} type': '{n} tipo',
    '{n} types': '{n} tipi',
    '{n} tag': '{n} tag',
    '{n} tags': '{n} tag',
    '<b>{n}</b> file is not online yet: it will be uploaded with the next "Save online" (automatic too).': '<b>{n}</b> file non è ancora online: verrà caricato con il prossimo "Salva online" (anche automatico).',
    '<b>{n}</b> files are not online yet: they will be uploaded with the next "Save online" (automatic too).': '<b>{n}</b> file non sono ancora online: verranno caricati con il prossimo "Salva online" (anche automatico).',
    '<b>{n}</b> file is not online yet: connect an online service from the Profiles window and save the profile.': '<b>{n}</b> file non è ancora online: collega un servizio online dalla finestra Profili e salva il profilo.',
    '<b>{n}</b> files are not online yet: connect an online service from the Profiles window and save the profile.': '<b>{n}</b> file non sono ancora online: collega un servizio online dalla finestra Profili e salva il profilo.',
    '<b>{n}</b> file is online but not yet on this device: press "Load online" or "Save online" to download it.': '<b>{n}</b> file è online ma non ancora su questo dispositivo: premi "Carica online" o "Salva online" per scaricarli.',
    '<b>{n}</b> files are online but not yet on this device: press "Load online" or "Save online" to download them.': '<b>{n}</b> file sono online ma non ancora su questo dispositivo: premi "Carica online" o "Salva online" per scaricarli.',

    // ===== app.js: navigation, footer, create button =====
    'Board': 'Board',
    'Calendar': 'Calendario',
    'Timeline': 'Timeline',
    'Hooks': 'Hooks',
    'Trends': 'Tendenze',
    'Keywords': 'Parole chiave',
    'Scripts': 'Script',
    'Cookie policy': 'Cookie policy',
    'Copyright': 'Copyright',
    // Legal pages (LegalPage.vue): the bar at the top when one is opened before choosing a profile
    'Open Hideo': 'Apri Hideo',
    'Add hook': 'Aggiungi Hook',
    'New Video': 'Nuovo Video',
    'New creator': 'Nuovo Creator',

    // ===== Profiles (stores/picker.ts, the profile dialogs) =====
    'Changes not saved in a file (last file {when})': 'Modifiche non salvate in un file (ultimo file {when})',
    'Changes not saved in a file': 'Modifiche non salvate in un file',
    'Never saved in a file': 'Mai salvato in un file',
    'Up to date: file saved': 'In pari: file salvato',
    'Up to date: file loaded': 'In pari: caricato il file',
    'Up to date: saved in the folder': 'In pari: salvato nella cartella',
    'Up to date: saved on {service}': 'In pari: salvato su {service}',
    'Up to date: loaded from {service}': 'In pari: caricato da {service}',
    'Up to date with the file': 'In pari con il file',
    '{status} · {when} from the {device}': '{status} · {when} dal {device}',
    '{status} from the {device}': '{status} dal {device}',
    // Profiles window (ProfilesDialog.vue)
    'Profiles': 'Profili',
    'Each profile has its own hooks, videos, calendar, creators, sources and scripts.': 'Ogni profilo ha i suoi hook, video, calendario, creators, fonti e script.',
    'Switch profile': 'Cambia profilo',
    'saves the profile with all its data to a file (plan, calendar, hooks, creators, sources, scripts, photos and bio)': 'salva in un file il profilo con tutti i suoi dati (PED, calendario, hook, creators, fonti, script, foto e bio)',
    'loads a file into the profile.': 'carica un file nel profilo.',
    'How to use the app on computer and phone:': 'Come usare l\'app su computer e telefono:',
    'ONLINE SAVING': 'SALVATAGGIO ONLINE',
    'Active profile': 'Profilo attivo',
    'Tap to switch to this profile': 'Tocca per passare a questo profilo',
    'Edit profile': 'Modifica profilo',
    'Save the profile with all its data in a file': 'Salva il profilo con tutti i dati in un file',
    'Load a file into this profile': 'Carica un file in questo profilo',
    'Delete profile': 'Elimina profilo',
    'Add profile': 'Aggiungi profilo',
    'Create profile': 'Crea profilo',
    'PROFILE NAME': 'NOME DEL PROFILO',
    'E.g. Mario': 'Es. Mario',
    'The project needs a name.': 'Il progetto ha bisogno di un nome.',
    'Photo, bio and socials can be added later, by editing the profile.': 'Foto, bio e social potrai aggiungerli dopo, modificando il profilo.',
    "Who's using Hideo?": 'Chi sta usando Hideo?',
    'Choose your profile': 'Scegli il tuo profilo',
    'Create your first profile to get started': 'Crea il tuo primo profilo per iniziare',
    'New profile': 'Nuovo profilo',
    'Save profile': 'Salva profilo',
    'The profile needs a name.': 'Il profilo deve avere un nome.',
    'A profile with this name already exists.': 'Esiste già un profilo con questo nome.',
    'Photo': 'Foto',
    'Remove photo': 'Rimuovi foto',
    'Who you are, what your channel is about...': 'Chi sei, di cosa parla il canale...',
    'Delete the profile "{name}" and all its data (ideas, videos, creators, sources, scripts) from this device?\n\nThe files already saved on Google Drive and the daily backups are not touched.': 'Eliminare il profilo "{name}" e tutti i suoi dati (idee, video, creators, fonti, script) da questo dispositivo?\n\nI file già salvati su Google Drive e i backup giornalieri non vengono toccati.',

    // ===== Projects: features/projects.ts, the project and plan dialogs (ProjectsDialog, PedViewDialog, FirstProjectDialog) =====
    'All projects': 'Tutti i progetti',
    'Project: {name}': 'Progetto: {name}',
    'Project: all': 'Progetto: tutti',
    'Empty': 'Vuoto',
    'PROJECTS': 'PROGETTI',
    'See everything together, with coloured labels': 'Vedi tutto insieme, con le etichette colorate',
    'Edit plan': 'Modifica PED',
    'New plan: choose which projects to see together': 'Nuovo PED: scegli quali progetti vedere insieme',
    'New plan': 'Nuovo PED',
    'A plan shows together, in the Board and the Calendar, the videos of the projects you tick: one, some or all. Each project keeps its own formats.': 'Un PED mostra insieme, in Board e Calendario, i video dei progetti che spunti: uno, alcuni o tutti. Ogni progetto tiene i suoi format.',
    'To see several projects together, first create another one from the project picker at the top (Manage projects).': 'Per vedere più progetti insieme creane prima un altro dal selettore del progetto in alto (Gestisci progetti).',
    'NAME': 'NOME',
    'E.g. Client work': 'Es. Lavori per clienti',
    'Save': 'Salva',
    'Delete the plan "{name}"? Videos and projects are not touched.': 'Eliminare il PED "{name}"? I video e i progetti non vengono toccati.',
    'It will be moved when you save': 'Verrà spostato quando salvi',
    'Choose another project to move it.': 'Scegli un altro progetto per spostarlo.',
    'Copy to another project': 'Copia in un altro progetto',
    'Copy to {name}': 'Copia in {name}',
    'Copied to "{name}"': 'Copiato in "{name}"',
    'Create your first project': 'Crea il tuo primo progetto',
    'A project is a channel, a client or a business: each has its own videos, hooks, creators and sources. Later you can create more and also see them all together.': 'Un progetto è un canale, un cliente o un\'attività: ognuno ha i suoi video, hook, creator e fonti. Più avanti potrai crearne altri e vederli anche tutti insieme.',
    'Everything you already have will go into this project.': 'Tutto quello che hai già finirà in questo progetto.',
    'PROJECT NAME': 'NOME DEL PROGETTO',
    'E.g. YouTube channel': 'Es. Canale YouTube',
    'COLOUR': 'COLORE',
    'Create project': 'Crea progetto',
    'Colour': 'Colore',
    'At least one project is needed': 'Serve almeno un progetto',
    'NEW PROJECT': 'NUOVO PROGETTO',
    'E.g. Cooking channel': 'Es. Canale cucina',
    'Every project has its own videos, formats, hooks, creators and sources. You choose the project to see at the top, next to the profile (on the phone, by tapping the profile); with "All projects" you see everything together. In the Board, with "+ Plan" you choose which projects to see together.': 'Ogni progetto ha i suoi video, format, hook, creator e fonti. Il progetto da vedere si sceglie in alto, accanto al profilo (sul telefono, toccando il profilo); con "Tutti i progetti" vedi tutto insieme. Nella Board, con "+ PED" scegli quali progetti vedere insieme.',
    'A project with this name already exists.': 'Esiste già un progetto con questo nome.',
    'Delete the project "{name}"?': 'Eliminare il progetto "{name}"?',
    'Its content ({counts}) will be moved to the project "{other}": no data is lost.': 'I suoi contenuti ({counts}) verranno spostati nel progetto "{other}": nessun dato viene perso.',
    '{n} video': '{n} video',
    '{n} videos': '{n} video',
    '{n} hook': '{n} hook',
    '{n} hooks': '{n} hook',
    '{n} creator': '{n} creator',
    '{n} creators': '{n} creator',
    '{n} source': '{n} fonte',
    '{n} sources': '{n} fonti'
}

export default it
